import React, { type ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon,
  MapIcon,
  ShieldExclamationIcon,
  FlagIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  EyeIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useNavigate } from 'react-router-dom';
import type {
  PePortfolioDashboardBundleDto,
  PePortfolioDashboardKeyAction,
  PePortfolioDashboardKeyIssue,
  PePortfolioDashboardKeyRisk,
} from '@/services/pePortfolioService';

export interface PortfolioDashboardPanelProps {
  bundle: PePortfolioDashboardBundleDto | null;
  loading: boolean;
  error: string | null;
  /** When true, show portfolio company name on each row (aggregated “all companies” view). */
  aggregateMode: boolean;
  onRetry: () => void;
  statsEnabled: boolean;
  keyRisksEnabled: boolean;
  keyIssuesEnabled: boolean;
  keyActionsEnabled: boolean;
  /**
   * When false, stat tiles and key risk/issue/action rows are display-only (no navigation).
   * Use on portfolio dashboard until child-context deep links are supported.
   */
  interactiveNavigationEnabled?: boolean;
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
      return 'text-red-600';
    case 'HIGH':
      return 'text-orange-600';
    case 'MEDIUM':
      return 'text-yellow-600';
    case 'LOW':
      return 'text-green-600';
    default:
      return 'text-[var(--color-secondary-text)]';
  }
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'CRITICAL':
    case 'HIGH':
      return ExclamationTriangleIcon;
    default:
      return ShieldExclamationIcon;
  }
};

const getUrgencyColor = (urgency: string) => {
  switch (urgency) {
    case 'CRITICAL':
      return 'text-red-600';
    case 'HIGH':
      return 'text-orange-600';
    case 'MEDIUM':
      return 'text-yellow-600';
    case 'LOW':
      return 'text-green-600';
    default:
      return 'text-[var(--color-secondary-text)]';
  }
};

const getUrgencyIcon = (urgency: string) => {
  switch (urgency) {
    case 'CRITICAL':
    case 'HIGH':
      return ExclamationTriangleIcon;
    default:
      return ExclamationCircleIcon;
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'OPEN':
      return 'text-red-600';
    case 'IN_PROGRESS':
      return 'text-blue-600';
    case 'RESOLVED':
      return 'text-green-600';
    default:
      return 'text-[var(--color-secondary-text)]';
  }
};

const getActionStatusColor = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return 'text-green-600';
    case 'IN_PROGRESS':
      return 'text-blue-600';
    case 'PENDING':
      return 'text-yellow-600';
    case 'CANCELLED':
      return 'text-gray-600';
    case 'RETURNED':
      return 'text-orange-600';
    default:
      return 'text-[var(--color-secondary-text)]';
  }
};

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString();
};

const formatActionDate = (dateString?: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDate = new Date(date);
  dueDate.setHours(0, 0, 0, 0);
  if (dueDate < today) {
    return `Overdue: ${date.toLocaleDateString()}`;
  }
  return date.toLocaleDateString();
};

export const PortfolioDashboardPanel: React.FC<PortfolioDashboardPanelProps> = ({
  bundle,
  loading,
  error,
  aggregateMode,
  onRetry,
  statsEnabled,
  keyRisksEnabled,
  keyIssuesEnabled,
  keyActionsEnabled,
  interactiveNavigationEnabled = true,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const metrics = bundle?.metrics;

  const stats: Array<{
    name: string;
    value: string;
    change: string;
    changeType: 'positive' | 'negative' | 'neutral';
    icon: ComponentType<{ className?: string }>;
    link: string;
  }> = [
    {
      name: t('dashboard.activeRoadmapsCount'),
      value: loading ? '…' : String(metrics?.activeRoadmaps ?? 0),
      change: '+0%',
      changeType: 'neutral',
      icon: MapIcon,
      link: '/tenant/roadmaps',
    },
    {
      name: t('dashboard.activeTransformationsCount'),
      value: loading ? '…' : String(metrics?.activeTransformations ?? 0),
      change: '+0%',
      changeType: 'neutral',
      icon: ChartBarIcon,
      link: '/transformations',
    },
    {
      name: t('dashboard.activeRisksCount'),
      value: loading ? '…' : String(metrics?.activeRisks ?? 0),
      change: '+0%',
      changeType: 'neutral' as const,
      icon: ShieldExclamationIcon,
      link: '/risk',
    },
    {
      name: t('dashboard.inchstonesProgress'),
      value: loading
        ? '…'
        : `${metrics?.inchstonesCompleted ?? 0}/${metrics?.inchstonesPlanned ?? 0}`,
      change:
        (metrics?.inchstonesPlanned ?? 0) > 0
          ? `${Math.round(((metrics?.inchstonesCompleted ?? 0) / (metrics?.inchstonesPlanned || 1)) * 100)}%`
          : '0%',
      changeType: 'positive',
      icon: FlagIcon,
      link: '/transformations',
    },
  ];

  const risks: PePortfolioDashboardKeyRisk[] = bundle?.keyRisks ?? [];
  const issues: PePortfolioDashboardKeyIssue[] = bundle?.keyIssues ?? [];
  const actions: PePortfolioDashboardKeyAction[] = bundle?.keyActions ?? [];

  if (error) {
    return (
      <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-6 text-center">
        <p className="text-sm text-red-600">{error}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 text-sm text-[var(--color-accent-text)] hover:underline"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <>
      {statsEnabled && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {stats.map((stat) => (
            <Card
              key={stat.name}
              className={
                interactiveNavigationEnabled
                  ? 'group hover:bg-[var(--color-accent-bg)] hover:bg-opacity-20 transition-colors bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-0 shadow-none cursor-pointer'
                  : 'bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-0 shadow-none cursor-default opacity-90'
              }
              onClick={
                interactiveNavigationEnabled && stat.link ? () => navigate(stat.link) : undefined
              }
              aria-disabled={!interactiveNavigationEnabled}
            >
              <CardContent className="px-4 py-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">
                      {stat.name}
                    </p>
                    <p className="text-xl font-bold text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)] transition-colors">
                      {stat.value}
                    </p>
                    <div className="flex items-center mt-0.5">
                      <span
                        className={`text-sm transition-colors ${
                          stat.changeType === 'positive'
                            ? 'text-[var(--color-success)]'
                            : stat.changeType === 'negative'
                              ? 'text-[var(--color-error)]'
                              : 'text-[var(--color-surface-text)] group-hover:text-[var(--color-accent-bg)]'
                        }`}
                      >
                        {stat.change}
                      </span>
                      <span className="text-xs text-[var(--color-surface-text)] opacity-75 ml-1 group-hover:text-[var(--color-accent-bg)] group-hover:opacity-100 transition-colors">
                        {t('dashboard.fromLastMonth')}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-[var(--color-accent-bg)]">
                    <stat.icon className="h-5 w-5 text-[var(--color-accent-text)]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
        {keyRisksEnabled && (
          <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none h-full">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)] flex items-center">
                <ShieldExclamationIcon className="h-5 w-5 mr-2" />
                {t('dashboard.keyRisks')}
              </CardTitle>
              <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
                {t('dashboard.keyRisksDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-text)] mx-auto" />
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">{t('common.loading')}</p>
                </div>
              ) : risks.length === 0 ? (
                <div className="text-center py-4">
                  <ShieldExclamationIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
                  <p className="text-sm opacity-75">{t('dashboard.noKeyRisks')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {risks.map((risk) => {
                    const SeverityIcon = getSeverityIcon(risk.severity);
                    const rowClass =
                      'group flex items-center justify-between p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]';
                    const rowInteractiveClass = interactiveNavigationEnabled
                      ? ' hover:bg-[var(--color-accent-bg)] hover:bg-opacity-10 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer'
                      : ' cursor-default opacity-90';
                    const body = (
                      <>
                        <div className="flex-1 min-w-0 mr-2">
                          {aggregateMode && risk.portfolio_company_name && (
                            <p className="text-[10px] uppercase tracking-wide text-[var(--color-secondary-text)] opacity-80 mb-0.5 truncate">
                              {risk.portfolio_company_name}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 mb-1">
                            <h4
                              className={`text-sm font-medium truncate transition-colors ${
                                interactiveNavigationEnabled
                                  ? 'group-hover:text-[var(--color-accent-text)]'
                                  : ''
                              }`}
                            >
                              {risk.name}
                            </h4>
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-surface-bg)] ${getSeverityColor(risk.severity)}`}
                            >
                              <SeverityIcon className="h-2.5 w-2.5 mr-0.5" />
                              {risk.severity}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 text-xs opacity-75">
                            <span className={getSeverityColor(risk.severity)}>{risk.status}</span>
                            {risk.due_date && (
                              <span className="flex items-center">
                                <CalendarIcon className="h-2.5 w-2.5 mr-0.5" />
                                {formatDate(risk.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                        <EyeIcon
                          className={`h-3.5 w-3.5 flex-shrink-0 opacity-50 ${interactiveNavigationEnabled ? 'group-hover:opacity-100' : ''}`}
                        />
                      </>
                    );
                    return interactiveNavigationEnabled ? (
                      <Link
                        key={risk.id}
                        to={`/risk?tab=pool&riskId=${risk.id}`}
                        className={rowClass + rowInteractiveClass}
                      >
                        {body}
                      </Link>
                    ) : (
                      <div
                        key={risk.id}
                        className={rowClass + rowInteractiveClass}
                        aria-disabled="true"
                      >
                        {body}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                {interactiveNavigationEnabled ? (
                  <Link
                    to="/risk"
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:bg-opacity-90 rounded-md transition-colors"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.viewAllRisks')}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] opacity-70 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.viewAllRisks')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {keyIssuesEnabled && (
          <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none h-full">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)] flex items-center">
                <ExclamationCircleIcon className="h-5 w-5 mr-2" />
                {t('dashboard.keyIssues')}
              </CardTitle>
              <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
                {t('dashboard.keyIssuesDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-text)] mx-auto" />
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">{t('common.loading')}</p>
                </div>
              ) : issues.length === 0 ? (
                <div className="text-center py-4">
                  <ExclamationCircleIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
                  <p className="text-sm opacity-75">{t('dashboard.noKeyIssues')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {issues.map((issue) => {
                    const UrgencyIcon = getUrgencyIcon(issue.urgency);
                    const rowClass =
                      'group flex items-center justify-between p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]';
                    const rowInteractiveClass = interactiveNavigationEnabled
                      ? ' hover:bg-[var(--color-accent-bg)] hover:bg-opacity-10 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer'
                      : ' cursor-default opacity-90';
                    const body = (
                      <>
                        <div className="flex-1 min-w-0 mr-2">
                          {aggregateMode && issue.portfolio_company_name && (
                            <p className="text-[10px] uppercase tracking-wide text-[var(--color-secondary-text)] opacity-80 mb-0.5 truncate">
                              {issue.portfolio_company_name}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 mb-1">
                            <h4
                              className={`text-sm font-medium truncate transition-colors ${
                                interactiveNavigationEnabled
                                  ? 'group-hover:text-[var(--color-accent-text)]'
                                  : ''
                              }`}
                            >
                              {issue.title}
                            </h4>
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-surface-bg)] ${getUrgencyColor(issue.urgency)}`}
                            >
                              <UrgencyIcon className="h-2.5 w-2.5 mr-0.5" />
                              {issue.urgency}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 text-xs opacity-75">
                            <span className={getStatusColor(issue.status)}>{issue.status}</span>
                            {issue.created_at && (
                              <span className="flex items-center">
                                <CalendarIcon className="h-2.5 w-2.5 mr-0.5" />
                                {formatDate(issue.created_at)}
                              </span>
                            )}
                          </div>
                        </div>
                        <EyeIcon
                          className={`h-3.5 w-3.5 flex-shrink-0 opacity-50 ${interactiveNavigationEnabled ? 'group-hover:opacity-100' : ''}`}
                        />
                      </>
                    );
                    return interactiveNavigationEnabled ? (
                      <Link
                        key={issue.id}
                        to={`/issues/${issue.id}`}
                        className={rowClass + rowInteractiveClass}
                      >
                        {body}
                      </Link>
                    ) : (
                      <div
                        key={issue.id}
                        className={rowClass + rowInteractiveClass}
                        aria-disabled="true"
                      >
                        {body}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                {interactiveNavigationEnabled ? (
                  <Link
                    to="/issues"
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:bg-opacity-90 rounded-md transition-colors"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.viewAllIssues')}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] opacity-70 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.viewAllIssues')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {keyActionsEnabled && (
          <Card className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] shadow-none h-full">
            <CardHeader>
              <CardTitle className="text-[var(--color-surface-text)] flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                {t('dashboard.keyActions')}
              </CardTitle>
              <CardDescription className="text-[var(--color-secondary-text)] opacity-75">
                {t('dashboard.keyActionsDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[var(--color-accent-text)] mx-auto" />
                  <p className="text-sm text-[var(--color-secondary-text)] opacity-75 mt-2">{t('common.loading')}</p>
                </div>
              ) : actions.length === 0 ? (
                <div className="text-center py-4">
                  <CheckCircleIcon className="h-12 w-12 opacity-50 mx-auto mb-2" />
                  <p className="text-sm opacity-75">{t('dashboard.noKeyActions')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {actions.map((action) => {
                    const rowClass =
                      'group flex items-center justify-between p-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-bg)]';
                    const rowInteractiveClass = interactiveNavigationEnabled
                      ? ' hover:bg-[var(--color-accent-bg)] hover:bg-opacity-10 hover:text-[var(--color-accent-text)] transition-colors cursor-pointer'
                      : ' cursor-default opacity-90';
                    const body = (
                      <>
                        <div className="flex-1 min-w-0 mr-2">
                          {aggregateMode && action.portfolio_company_name && (
                            <p className="text-[10px] uppercase tracking-wide text-[var(--color-secondary-text)] opacity-80 mb-0.5 truncate">
                              {action.portfolio_company_name}
                            </p>
                          )}
                          <div className="flex items-center space-x-2 mb-1">
                            <h4
                              className={`text-sm font-medium truncate transition-colors ${
                                interactiveNavigationEnabled
                                  ? 'group-hover:text-[var(--color-accent-text)]'
                                  : ''
                              }`}
                            >
                              {action.title}
                            </h4>
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-surface-bg)] text-[var(--color-secondary-text)]">
                              {action.priority}
                            </span>
                          </div>
                          <div className="flex items-center space-x-3 text-xs opacity-75">
                            <span className={getActionStatusColor(action.status)}>{action.status}</span>
                            {action.due_date && (
                              <span className="flex items-center">
                                <CalendarIcon className="h-2.5 w-2.5 mr-0.5" />
                                {formatActionDate(action.due_date)}
                              </span>
                            )}
                            {action.created_by_user_name && (
                              <span className="flex items-center">
                                <UserIcon className="h-2.5 w-2.5 mr-0.5" />
                                {action.created_by_user_name}
                              </span>
                            )}
                          </div>
                        </div>
                        <EyeIcon
                          className={`h-3.5 w-3.5 flex-shrink-0 opacity-50 ${interactiveNavigationEnabled ? 'group-hover:opacity-100' : ''}`}
                        />
                      </>
                    );
                    return interactiveNavigationEnabled ? (
                      <Link
                        key={action.id}
                        to={`/actions/${action.id}`}
                        className={rowClass + rowInteractiveClass}
                      >
                        {body}
                      </Link>
                    ) : (
                      <div
                        key={action.id}
                        className={rowClass + rowInteractiveClass}
                        aria-disabled="true"
                      >
                        {body}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
                {interactiveNavigationEnabled ? (
                  <Link
                    to="/actions"
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] hover:bg-opacity-90 rounded-md transition-colors"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.viewAllActions')}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)] opacity-70 cursor-not-allowed"
                    aria-disabled="true"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 mr-1.5" />
                    {t('dashboard.viewAllActions')}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};
